import { Badge, Box, Button, Grid, Link, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { GridActionsCellItem, GridRowHeightParams, GridSelectionModel, useGridApiRef } from '@mui/x-data-grid-pro';
import { DataTable } from '@pihealth/common-ui-library';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { usePermissionWrapper } from 'src/components/hooks/usePermissionWrapper';
import { useUtil } from 'src/components/hooks/useUtil';
import AppModal from 'src/components/modal';
import {
  useCreateDrugSuppplyDocMutation,
  useDeleteSuppplyDocMutation,
  useGetAllPendingShipmentMutation,
  useGetCountryAccessMutation,
  useUpdateDrugSupplyStatusMutation
} from 'src/services/DrugSupply';
import { useGetCodeListDataQuery } from 'src/services/codeList';
import { useAppSelector } from 'src/store';
import { setMessage } from 'src/store/Message';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { setShippedData } from 'src/store/drugSupplySlice';
import ShippedDialog from '../DrugSupply/ShippedDialog';
import { hideLoader, showLoader } from 'src/store/Loader';
import { useGetAllDocumentsByArtifactnameStudylevelMutation } from 'src/services/etmfDocLevel';
import DocumentUploadModel from 'src/components/addDocumentModel/DocumentUploadModel';

const PendingShipment = () => {
  const { data: codeListData } = useGetCodeListDataQuery('IPSSTAT');
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const apiRef = useGridApiRef();
  const [paginationVal, setPaginationVal] = useState(true);
  const [pageSize, setPageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(0);
  const [selectedRowData, setSelectedRowData] = useState<any>([]);
  const [open, setOpen] = useState(false);
  const [row, setRow] = useState([]);
  const [filterObject, setFilterObject] = useState<any>();
  const selectedStudy: string | null = useAppSelector(state => state.site.selectedStudy);
  const { handleError } = useUtil();
  const [rowsCount, setRowsCount] = useState(0);
  const dispatch = useDispatch();
  const { checkPermission } = usePermissionWrapper();
  const [openShipped, setOpenShipped] = useState<boolean>(false);
  const [getDocsByArtifactNameStudyLevel] = useGetAllDocumentsByArtifactnameStudylevelMutation();
  const [attachedDocsList, setAttachedDocsList] = useState<any[]>([]);
  const [uploadDocDialogOpen, setUploadDocDilogOpen] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState('');
  const [createDrugSupplyDoc, { isSuccess: createDrugSupplyDocSuccess, data: createDrugDocData }] =
    useCreateDrugSuppplyDocMutation();
  const [deleteDrugSupplyDoc, { isSuccess: deleteDrugSupplyDocSuccess }] = useDeleteSuppplyDocMutation();
  const [
    updateDStatusFunc,
    { isSuccess: updateDStatusIsSuccess, isError: updateDStatusIsError, error: updateDStatusError }
  ] = useUpdateDrugSupplyStatusMutation();

  const [
    getAllPendingShipment,
    {
      data: getAllPendingShipmentData,
      isLoading: getAllPendingShipmentLoading,
      isError: getAllPendingShipmentIsError,
      error: getAllPendingShipmentError,
      isSuccess: getAllPendingShipmentIsSuccess
    }
  ] = useGetAllPendingShipmentMutation();
  const [getCountryAccess] = useGetCountryAccessMutation();

  const pendingShipmentColumns = [
    {
      field: 'shipment_id',
      headerName: 'Shipment ID',
      minWidth: 120,
      flex: 1
    },
    {
      field: 'depot_name',
      headerName: 'Depot Name',
      minWidth: 120,
      flex: 1
    },
    {
      field: 'depot_country',
      headerName: 'Depot Country',
      minWidth: 140,
      flex: 1
    },
    {
      field: 'depot_id',
      headerName: 'Depot ID',
      minWidth: 180,
      flex: 1
    },
    {
      field: 'custom_name',
      headerName: 'Custom Name',
      minWidth: 140,
      flex: 1
    },
    {
      field: 'cost_per_unit',
      headerName: 'Cost Per Unit',
      width: 100
    },
    {
      field: 'investigational_drug',
      headerName: 'Investigational Drug',
      minWidth: 100,
      flex: 1
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      description: 'Quantity available at this Depot',
      width: 80,
      type: 'number',
      valueFormatter: (params: any) => {
        return params.value;
      }
    },
    {
      field: 'quantity_retained',
      headerName: 'Quantity Retained',
      description: 'Quantity withheld at this Depot',
      width: 140,
      type: 'number',
      defaultValue: 0,
      valueFormatter: (params: any) => {
        return params.value;
      }
    },
    {
      field: 'transfer_quantity',
      headerName: 'Transfer Quantity',
      description: 'Quantity of Drug being transferred from this Depot',
      width: 140,
      type: 'number',
      defaultValue: 0,
      valueFormatter: (params: any) => {
        return params.value;
      }
    },
    {
      field: 'dose',
      headerName: 'Dose',
      width: 100,
      type: 'number'
    },
    {
      field: 'dose_unit',
      headerName: 'Dose Unit',
      width: 80
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      type: 'singleSelect',
      renderCell: (params: any) => handleClickStatusLink(params.row),
      valueOptions: codeListData?.map((item: any) => item?.code_list_value)
    },
    {
      field: 'manufacturer',
      headerName: 'Manufacturer',
      minWidth: 100,
      flex: 1
    },
    {
      field: 'manufacturing_lot',
      headerName: 'Manufacturing Lot #',
      minWidth: 140,
      flex: 1
    },
    {
      field: 'packaging_lot',
      headerName: 'Packaging Lot #',
      minWidth: 120,
      flex: 1
    },
    {
      field: 'earliest_expiration_date',
      headerName: 'Earliest Expiration Date',
      minWidth: 150,
      flex: 1
    },
    {
      field: 'latest_expiration_date',
      headerName: 'Latest Expiration Date',
      minWidth: 150,
      flex: 1,
      type: 'date'
    },
    {
      field: 'number_of_kits',
      headerName: 'Number of Kits',
      width: 120
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      filterable: false,
      disableExport: true,
      renderCell: (params: any) => {
        const id = params.id;
        return [
          <GridActionsCellItem
            icon={
              params.row.status === 'Shipped' ? (
                <Tooltip title="Drug supply record can not be edited with the status 'SHIPPED'">
                  <EditIcon />
                </Tooltip>
              ) : (
                <EditIcon />
              )
            }
            label="Edit"
            disabled={!checkPermission('manage_drug_supply')}
            sx={{
              color: 'primary.main',
              fontSize: '1.5rem'
            }}
            onClick={handleEditClick(id, params.row)}
            key={id}
          />,
          <GridActionsCellItem
            icon={
              params.row.drug_supply_file_data && params.row.drug_supply_file_data.length > 0 ? (
                <Badge
                  badgeContent={params.row.drug_supply_file_data.length}
                  color="primary"
                  className="smallBadgeSearch"
                >
                  {' '}
                  <AttachFileIcon />{' '}
                </Badge>
              ) : (
                <AttachFileIcon />
              )
            }
            label="Attach"
            className="textPrimary"
            onClick={() => handleAttachClick(id, params.row.drug_supply_file_data)}
            key={2}
            sx={{
              color: 'primary.main'
            }}
            color="inherit"
            disabled={!checkPermission('view_drug_supply')}
          />,
          <GridActionsCellItem
            key={id}
            icon={
              params.row.status === 'Shipped' ? (
                <Tooltip title="Drug supply record can not be deleted with the status 'SHIPPED'">
                  <DeleteIcon />
                </Tooltip>
              ) : (
                <DeleteIcon />
              )
            }
            label="Delete"
            disabled={!checkPermission('manage_drug_supply')}
            onClick={handleDeleteClick(id, params.row)}
            sx={{
              color: 'primary.main',
              fontSize: '1.5rem'
            }}
          />
        ];
      }
    }
  ];

  const filterValues = (data: any) => {
    setFilterObject(data);
  };

  useEffect(() => {
    if (!getAllPendingShipmentLoading && (pageSize === 10 || pageSize === 20 || pageSize === 30)) {
      if (pageNumber * pageSize > rowsCount - 1) {
        setPageNumber(0);
        getAllPendingShipment({
          study_id: selectedStudy,
          application: process.env.REACT_APP_NAME,
          application_section: process.env.REACT_APP_TUDA_SLUG,
          application_module_section: process.env.REACT_APP_CONDUCT_NAME,
          data: { ...filterObject },
          from: 0,
          size: pageSize
        });
      } else {
        getAllPendingShipment({
          study_id: selectedStudy,
          application: process.env.REACT_APP_NAME,
          application_section: process.env.REACT_APP_TUDA_SLUG,
          application_module_section: process.env.REACT_APP_CONDUCT_NAME,
          data: { ...filterObject },
          from: pageNumber * pageSize,
          size: pageSize
        });
      }
    }
  }, [pageNumber, pageSize, filterObject]);

  useEffect(() => {
    if ((createDrugSupplyDocSuccess && createDrugDocData) || deleteDrugSupplyDocSuccess) {
      getAllPendingShipment({
        study_id: selectedStudy,
        application: process.env.REACT_APP_NAME,
        application_section: process.env.REACT_APP_TUDA_SLUG,
        application_module_section: process.env.REACT_APP_CONDUCT_NAME,
        data: { ...filterObject },
        from: pageNumber * pageSize,
        size: pageSize
      });
    }
    if (createDrugSupplyDocSuccess) {
      dispatch(setMessage({ msgDetails: 'Document uploaded successfully', severity: 'success' }));
    }
    if (deleteDrugSupplyDocSuccess) {
      dispatch(setMessage({ msgDetails: 'Document deleted successfully.', severity: 'success' }));
    }
  }, [createDrugSupplyDocSuccess, deleteDrugSupplyDocSuccess]);

  const dataTableProps = {
    apiRef,
    filterMode: 'server',
    onRowsSelection: (newSelection: any) => setSelectionModel(newSelection),
    getRowHeight: ({ id, densityFactor }: GridRowHeightParams) => {
      if ((id as number) % 2 === 0) {
        return 100 * densityFactor;
      }
      return null;
    },
    setPaginationValue: (hideColumsArr: any) => {
      if (hideColumsArr.includes(true)) {
        setPaginationVal(true);
      } else {
        setPaginationVal(false);
      }
    }
  };

  useEffect(() => {
    setRow([]);
    getAllPendingShipment({
      study_id: selectedStudy,
      application: process.env.REACT_APP_NAME,
      application_section: process.env.REACT_APP_TUDA_SLUG,
      application_module_section: process.env.REACT_APP_CONDUCT_NAME,
      data: { ...filterObject },
      from: 0,
      size: pageSize
    });
  }, [selectedStudy]);

  useEffect(() => {
    if (getAllPendingShipmentIsSuccess) {
      const data = getAllPendingShipmentData?.selected_data?.map(
        (item: { drug_supply_data: any; drug_supply_file_data: any }) => {
          return { ...item.drug_supply_data, drug_supply_file_data: item.drug_supply_file_data };
        }
      );
      setRow(data);
      setRowsCount(getAllPendingShipmentData.record_count);
    }
  }, [getAllPendingShipmentIsSuccess]);

  useEffect(() => {
    if (getAllPendingShipmentIsError || updateDStatusIsError) {
      handleError(getAllPendingShipmentError || updateDStatusError);
      setRow([]);
      setRowsCount(0);
    }
  }, [getAllPendingShipmentIsError, updateDStatusIsError]);
  useEffect(() => {
    if (selectionModel && selectionModel?.length > 1) {
      dispatch(setMessage({ msgDetails: 'Multiple drug supplies cannot be received', severity: 'info' }));
    }
  });

  const onSelectionModelChange = (ids: any) => {
    setSelectionModel(ids);
    const selectedIds = new Set(ids);
    const filteredFormIds: any = row.filter((row: any) => selectedIds.has(row.id));
    setSelectedRowData(filteredFormIds);
  };

  const handleReceive = async () => {
    const isAccess: any = await getCountryAccess({
      transfer_to: selectedRowData[0]?.transfer_to,
      study_id: selectedStudy,
      application: 'CTMS',
      application_section: process.env.REACT_APP_TUDA_SLUG,
      application_module_section: process.env.REACT_APP_CONDUCT_NAME
    });
    if (isAccess?.data) {
      updateDStatusFunc({
        drug_supply_id: selectedRowData[0]?.id,
        study_id: selectedStudy,
        application: 'CTMS',
        application_section: process.env.REACT_APP_TUDA_SLUG,
        application_module_section: process.env.REACT_APP_CONDUCT_NAME,
        data: {
          status: 'Available'
        }
      });
    } else {
      dispatch(setMessage({ msgDetails: 'No country access for the user.', severity: 'error' }));
      setOpen(false);
      setSelectionModel([]);
    }
  };

  useEffect(() => {
    if (updateDStatusIsSuccess) {
      setOpen(false);
      dispatch(setMessage({ msgDetails: 'Drug Supply Received Successfully', severity: 'success' }));
      getAllPendingShipment({
        study_id: selectedStudy,
        application: process.env.REACT_APP_NAME,
        application_section: process.env.REACT_APP_TUDA_SLUG,
        application_module_section: process.env.REACT_APP_CONDUCT_NAME,
        data: { ...filterObject },
        from: pageNumber * pageSize,
        size: pageSize
      });
    }
  }, [updateDStatusIsSuccess]);

  const handleDeleteClick = (id: any, rowData: any) => () => {
    if (rowData?.status === 'Shipped') {
      dispatch(
        setMessage({ msgDetails: "Drug supply record can not be deleted with the status 'SHIPPED'", severity: 'info' })
      );
    }
  };

  const handleEditClick = (id: any, rowData: any) => () => {
    if (rowData?.status === 'Shipped') {
      dispatch(
        setMessage({ msgDetails: "Drug supply record can not be edited with the status 'SHIPPED'", severity: 'info' })
      );
    }
  };

  const handleClickStatusLink = (rowData: any) => {
    return rowData.status === 'Shipped' ? (
      <Link onClick={() => handleClickShipped(rowData)}>{rowData.status}</Link>
    ) : (
      rowData.status
    );
  };
  const handleClickShipped = (data: any) => {
    setOpenShipped(true);
    dispatch(
      setShippedData({
        ...data,
        transfer_quantity: data?.transfer_quantity,
        transfer_from: `${data?.depot_name} - ${data?.depot_id}`
      })
    );
  };

  const handleAttachClick = async (recordId: string, filesAttached: any[]) => {
    dispatch(showLoader());
    const tmfDocIdsList = filesAttached.length ? filesAttached.map((file: any) => file.tmf_document_id) : [];
    const docsListResponse: any = await getDocsByArtifactNameStudyLevel({
      data: {
        filters: [
          {
            name: 'tmf_document_id',
            value: tmfDocIdsList,
            operation: 'in'
          }
        ],
        type: 'AND'
      },
      docLevel: 'Study',
      study_id: selectedStudy,
      application: process.env.REACT_APP_NAME,
      module_section_related: process.env.REACT_APP_NAME,
      parent_entity_related: ''
    });
    if (docsListResponse?.data) {
      setAttachedDocsList(docsListResponse?.data?.selected_data);
    } else if (docsListResponse?.error && docsListResponse?.error.status !== 404) {
      setAttachedDocsList([]);
      dispatch(setMessage({ msgDetails: 'Error getting list of documents attached', severity: 'error' }));
    }
    setUploadDocDilogOpen(true);
    setSelectedRecordId(recordId);
    dispatch(hideLoader());
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, mt: -1 }}>
        <Typography variant="h6" component="h6">
          Transit Shipments
        </Typography>
        <Button
          variant="contained"
          onClick={() => setOpen(true)}
          size="small"
          sx={{ alignSelf: 'center' }}
          disabled={(selectionModel && selectionModel?.length != 1) || !checkPermission('mark_as_received')}
        >
          MARK AS RECEIVED<span> &nbsp;{selectedRowData.length == 1 && `(1)`}</span>
        </Button>
      </Box>
      <DataTable
        height="75.75vh"
        rows={row || []}
        columns={pendingShipmentColumns}
        customeMessage={
          <Stack height="80%" alignItems="center" justifyContent="center">
            No pending shipments available in the grid view
          </Stack>
        }
        pinnedColumns={{ right: ['actions'] }}
        exportCSVName={'Pending Shipment'}
        hasDefinedColumns
        checkboxSelection
        dataTableProps={dataTableProps}
        onRowsSelection={onSelectionModelChange}
        selectionModel={selectionModel}
        pageSize={pageSize}
        setPageSize={setPageSize}
        loading={getAllPendingShipmentLoading}
        pagination={paginationVal}
        filterValues={(data: any) => filterValues(data)}
        paginationMode="server"
      />
      <AppModal
        open={open}
        title={'Mark as Received'}
        child
        saveButton={'Receive'}
        closeButton={'Cancel'}
        handleSubmit={handleReceive}
        handleClose={() => setOpen(false)}
        maxWidth="600px"
      >
        <Grid container spacing={2} rowSpacing={2} mb={4} sx={{ wordBreak: 'break-word' }}>
          <Grid item md={6}>
            <Typography variant="body1" sx={{ fontSize: 'clamp(14px, 15px, 16px)' }}>
              <strong style={{ display: 'inline-block', minWidth: 'fit-content' }}>Custom Name:&nbsp;</strong>
              <span className="results">{selectedRowData[0]?.custom_name}</span>
            </Typography>
          </Grid>
          <Grid item md={6}>
            <Typography variant="body1" sx={{ fontSize: 'clamp(14px, 15px, 16px)' }}>
              <strong style={{ display: 'inline-block', minWidth: 'fit-content' }}>Investigational Drug:&nbsp;</strong>
              <span className="results">{selectedRowData[0]?.investigational_drug}</span>
            </Typography>
          </Grid>
          <Grid item md={6}>
            <Typography variant="body1" sx={{ fontSize: 'clamp(14px, 15px, 16px)' }}>
              <strong style={{ display: 'inline-block', minWidth: 'fit-content' }}>Manufacturing Lot #:&nbsp;</strong>
              <span className="results">{selectedRowData[0]?.manufacturing_lot}</span>
            </Typography>
          </Grid>
          <Grid item md={6}>
            <Typography variant="body1" sx={{ fontSize: 'clamp(14px, 15px, 16px)' }}>
              <strong style={{ display: 'inline-block', minWidth: 'fit-content' }}>Quantity:&nbsp;</strong>
              <span className="results">{selectedRowData[0]?.transfer_quantity}</span>
            </Typography>
          </Grid>
          <Grid item md={12} pt={1}>
            <TextField
              label="Transfer From"
              variant="standard"
              sx={{ fontSize: 'clamp(14px, 15px, 16px)' }}
              value={`${selectedRowData[0]?.depot_name} - ${selectedRowData[0]?.depot_id}`}
              disabled
              fullWidth
            />
          </Grid>
          <Grid item md={12}>
            <TextField
              label="Transfer To"
              variant="standard"
              sx={{ fontSize: 'clamp(14px, 15px, 16px)' }}
              value={selectedRowData[0]?.transfer_to}
              disabled
              fullWidth
            />
          </Grid>
          {/* <Grid item md={12}>
            <TextField
              label="Transfer Quantity"
              variant="standard"
              sx={{ fontSize: 'clamp(14px, 15px, 16px)' }}
              value={selectedRowData[0]?.transfer_quantity}
              disabled
              fullWidth
            />
          </Grid> */}
          <Grid item md={12}>
            <TextField
              label="Shipment ID"
              variant="standard"
              sx={{ fontSize: 'clamp(14px, 15px, 16px)' }}
              value={selectedRowData[0]?.shipment_id}
              disabled
              fullWidth
            />
          </Grid>
        </Grid>

        <br />
        <br />
      </AppModal>
      {openShipped && <ShippedDialog openShipped={openShipped} setOpenShipped={setOpenShipped} />}
      <DocumentUploadModel
        uploadDocDialogOpen={uploadDocDialogOpen}
        setUploadDocDilogOpen={setUploadDocDilogOpen}
        moduleSection={process.env.REACT_APP_CONDUCT_NAME || ''}
        createDrugSupplyDoc={createDrugSupplyDoc}
        deleteDrugSupplyDoc={deleteDrugSupplyDoc}
        selectedRecordId={selectedRecordId}
        attachedDocumentsList={attachedDocsList}
        setAttachedDocsList={setAttachedDocsList}
      />
    </>
  );
};

export default PendingShipment;
